import React from "react";
import {
    Box,
    Button, FormControl, FormErrorMessage, FormLabel, Grid, Input, Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Stack,
    Text,
    useColorModeValue,
    GridItem,
    Select,
    Textarea
} from "@chakra-ui/react";
import {
    MdAdd,
    MdArrowBack,
    MdSave,
} from "react-icons/md";
import { SingleDatepicker } from "chakra-dayzed-datepicker";
import { useLaunchedProject } from "../hooks/useLaunchedProject";
import { defaultCols } from "models/tasks_type";

type NewTaskFormProps = Pick<useLaunchedProject.type, 'state' | 'closeKamban' | 'displayInputError' | 'changeInput' | 'setShowTaskForm' | 'saveTask'>;

const NewTaskForm: React.FC<NewTaskFormProps> = ({ state, closeKamban, changeInput, displayInputError, setShowTaskForm, saveTask }) => {

    const textColor = useColorModeValue("navy.700", "white");
    const brandStars = useColorModeValue("brand.500", "brand.400");

    return (
        <>
            <Box
                w={"100%"}
                h={"16"}
                display={"flex"}
                alignItems={"center"}
                p={4}
                gap={4}
                mb={4}
                backgroundColor={"gray.100"}
                borderRadius={"lg"}
            >
                <Button
                    leftIcon={<MdArrowBack />}
                    colorScheme='brand'
                    size={"sm"}
                    borderRadius={'lg'}
                    variant='solid'
                    onClick={closeKamban}
                    isLoading={state.loading}
                >
                    Voltar
                </Button>

                <Button
                    leftIcon={<MdAdd />}
                    colorScheme='brand'
                    size={"sm"}
                    borderRadius={'lg'}
                    variant='outline'
                    onClick={() => setShowTaskForm(true)}
                    isLoading={state.loading}
                >
                    Adicionar
                </Button>
            </Box>

            <Modal
                onClose={() => {
                    setShowTaskForm(false);
                }}
                size={"3xl"}
                isOpen={state.showTaskForm}
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {state.taskForm.id
                            ? "Editar Task"
                            : "Nova Task"}
                    </ModalHeader>

                    <Box
                        px="0px"
                        mb="16px"
                        mt="0px"
                        justifyContent="space-between"
                        ml={4}
                    >
                        <Stack direction="row" spacing={4}>
                            <Button
                                leftIcon={<MdSave />}
                                colorScheme="brand"
                                size={"sm"}
                                variant="solid"
                                isLoading={state.loading}
                                onClick={saveTask}
                            >
                                Salvar
                            </Button>
                        </Stack>
                    </Box>

                    <ModalCloseButton />
                    <ModalBody>

                        <Grid
                            templateColumns='repeat(4, 1fr)'
                            templateRows='repeat(4, 1fr)'
                            gap={4}
                            mb={4}
                        >
                            <GridItem colSpan={1} rowSpan={1} >
                                <FormControl>
                                    <FormLabel
                                        display="flex"
                                        ms="4px"
                                        fontSize="sm"
                                        fontWeight="500"
                                        color={textColor}
                                        mb="8px"
                                    >
                                        Código
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        ms={{ base: "0px", md: "0px" }}
                                        type="text"
                                        isDisabled={true}
                                        fontWeight="500"
                                        defaultValue={state.taskForm.code}
                                        size="md"
                                    />
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={3} rowSpan={1} >
                                <FormControl isInvalid={displayInputError("titulo").error}>
                                    <FormLabel
                                        display="flex"
                                        ms="4px"
                                        fontSize="sm"
                                        fontWeight="500"
                                        color={textColor}
                                        mb="8px"
                                    >
                                        Titulo<Text color={brandStars}>*</Text>
                                    </FormLabel>
                                    <Input
                                        isRequired={true}
                                        fontSize="sm"
                                        ms={{ base: "0px", md: "0px" }}
                                        type="text"
                                        placeholder="Preencha o Titulo"
                                        fontWeight="500"
                                        defaultValue={state.taskForm.titulo}
                                        size="md"
                                        autoFocus={true}
                                        onBlur={(e) => changeInput({ type: "titulo", payload: e.target.value })}
                                    />

                                    {displayInputError("titulo").error && (
                                        <FormErrorMessage>{displayInputError("titulo").errorMessage}</FormErrorMessage>
                                    )}
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={4} rowSpan={4} >
                                <FormControl isInvalid={displayInputError("descricao").error}>
                                    <FormLabel
                                        display="flex"
                                        ms="4px"
                                        fontSize="sm"
                                        fontWeight="500"
                                        color={textColor}
                                        mb="8px"
                                    >
                                        Descricao<Text color={brandStars}>*</Text>
                                    </FormLabel>

                                    <Textarea
                                        defaultValue={state.taskForm.descricao}
                                        fontSize="sm"
                                        height={200}
                                        ms={{ base: "0px", md: "0px" }}
                                        placeholder="Preencha a Descricao"
                                        onBlur={(e) => {
                                            changeInput({ type: "descricao", payload: e.target.value });
                                        }}
                                        fontWeight="500"
                                        size="md"
                                    />

                                    {displayInputError("descricao").error && (
                                        <FormErrorMessage>{displayInputError("descricao").errorMessage}</FormErrorMessage>
                                    )}
                                </FormControl>
                            </GridItem>


                            <GridItem colSpan={1} rowSpan={1} >
                                <FormControl isInvalid={displayInputError("dataEstimativa").error}>
                                    <FormLabel
                                        display="flex"
                                        ms="4px"
                                        fontSize="sm"
                                        fontWeight="500"
                                        color={textColor}
                                        mb="8px"
                                    >
                                        Estimativa<Text color={brandStars}>*</Text>
                                    </FormLabel>

                                    <SingleDatepicker
                                        name="date-input-validade"
                                        configs={{
                                            dateFormat: "dd/MM/yyyy",
                                            dayNames: "DSTQQSS".split(""), // length of 7
                                            monthNames:
                                                "JANEIRO FEVEREIRO MARÇO ABRIL MAIO JUNHO JULHO AGOSTO SETEMBRO OUTUBRO NOVEMBRO DEZEMBRO".split(
                                                    " "
                                                ), // length of 12
                                            firstDayOfWeek: 0,
                                            // default is 0, the dayNames[0], which is Sunday if you don't specify your own dayNames,
                                        }}
                                        propsConfigs={{
                                            dayOfMonthBtnProps: {
                                                selectedBtnProps: {
                                                    background: "#422afb",
                                                    color: "white",
                                                },
                                            },
                                        }}
                                        date={state.taskForm.dataEstimativa}
                                        onDateChange={(date: any) => {
                                            changeInput({ type: "dataEstimativa", payload: date })
                                        }}
                                    />

                                    {displayInputError("dataEstimativa").error && (
                                        <FormErrorMessage>{displayInputError("dataEstimativa").errorMessage}</FormErrorMessage>
                                    )}
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={2} rowSpan={1} >
                                <FormControl isInvalid={displayInputError("userResponsavelId").error}>
                                    <FormLabel
                                        display="flex"
                                        ms="4px"
                                        fontSize="sm"
                                        fontWeight="500"
                                        color={textColor}
                                        mb="8px"
                                    >
                                        Responsavel<Text color={brandStars}>*</Text>
                                    </FormLabel>

                                    <Select
                                        size="md"
                                        defaultValue={state.taskForm.userResponsavelId}
                                        onChange={(e) => changeInput({ type: "userResponsavelId", payload: e.target.value })}
                                        placeholder={'Selecione o Responsavel'}
                                    >
                                        {state.users?.map((item) => (
                                            <option key={item.id} value={item.id}>{item.nome}</option>
                                        ))}
                                    </Select>

                                    {displayInputError("userResponsavelId").error && (
                                        <FormErrorMessage>{displayInputError("userResponsavelId").errorMessage}</FormErrorMessage>
                                    )}
                                </FormControl>
                            </GridItem>

                            <GridItem colSpan={1} rowSpan={1} >
                                <FormControl>
                                    <FormLabel
                                        display="flex"
                                        ms="4px"
                                        fontSize="sm"
                                        fontWeight="500"
                                        color={textColor}
                                        mb="8px"
                                    >
                                        Status<Text color={brandStars}>*</Text>
                                    </FormLabel>

                                    <Select
                                        size="md"
                                        defaultValue={state.taskForm.status}
                                        onChange={(e) => changeInput({ type: "status", payload: e.target.value })}
                                    >
                                        {defaultCols?.map((item) => (
                                            <option key={item.id} value={item.id}>{item.title}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </GridItem>

                        </Grid>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}

export default NewTaskForm;